"use client";

import Link from "next/link";
import { useMemo } from "react";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/components/providers/auth-provider";
import { useLibrary } from "@/features/dashboard/hooks";
import { cn } from "@/lib/utils";
import {
  coalesceCourseMoney,
  formatCourseMoneyEnIn,
  getLocalizedField,
  type Course,
  type CoursePrice,
} from "../api";
import { useCoursePlanPricingUiModel } from "../hooks/use-course-plan-pricing-ui-model";
import {
  formatTierUpgradeCtaLabel,
  isPlanIdActiveUpgradeTarget,
  resolvePlanPricingUiKind,
  resolvePricingUiOwnedState,
} from "../lib/resolve-course-plan-pricing-ui-state";
import { getOwnershipCopy } from "../lib/ownership-copy";
import type { Locale } from "@/lib/i18n";
import { CourseEnrollCta } from "./CourseEnrollCta";
import { CoursePricingOwnedStatusBanner } from "./CoursePricingOwnedStatusBanner";
import { useCoursePricing } from "./CoursePricingProvider";
import { formatBdt, fromPriceLabel, resolveCardPrice } from "../lib/card-price";
import { useCourseEligibilityStatus } from "../use-course-eligibility-status";
import { scrollToCoursePricingSection } from "../lib/scroll-course-pricing";
import { signInHref } from "@/lib/post-login-redirect";

function PriceDisplay({
  price,
  size = "lg",
  planSummary,
  locale,
}: {
  price: CoursePrice;
  size?: "sm" | "lg";
  /** The course's active-plan summary; wins over `price` until a plan is selected. */
  planSummary?: { planCount?: number | undefined; fromPrice?: number | null | undefined };
  locale: Locale;
}) {
  const planPrice = planSummary ? resolveCardPrice({ price, ...planSummary }) : null;
  if (planPrice && (planPrice.kind === "from" || planPrice.kind === "single")) {
    return (
      <span
        className={cn(
          "font-bold text-neutral-950",
          size === "lg" ? "text-[32px]" : "text-[20px]",
        )}
      >
        {planPrice.kind === "from"
          ? fromPriceLabel(planPrice.amount, locale)
          : formatBdt(planPrice.amount)}
      </span>
    );
  }
  const list = coalesceCourseMoney(price.list);
  const sale = price.sale;
  const hasSale =
    !price.isFree && sale !== undefined && Number.isFinite(sale) && sale < list;

  if (price.isFree) {
    return (
      <span
        className={cn(
          "font-bold text-emerald-600",
          size === "lg" ? "text-[32px]" : "text-[20px]",
        )}
      >
        Free
      </span>
    );
  }

  const currency = price.currency === "BDT" ? "৳" : "$";

  return (
    <div className="flex flex-wrap items-center justify-center gap-2.5">
      {hasSale && (
        <span
          className={cn(
            "font-bold text-neutral-950",
            size === "lg" ? "text-[32px]" : "text-[20px]",
          )}
        >
          {currency}
          {formatCourseMoneyEnIn(sale)}
        </span>
      )}
      <span
        className={cn(
          hasSale ? "text-neutral-400 line-through" : "font-bold text-neutral-950",
          size === "lg" && !hasSale
            ? "text-[32px]"
            : hasSale
              ? "text-[18px]"
              : "text-[20px]",
        )}
      >
        {currency}
        {formatCourseMoneyEnIn(list)}
      </span>
      {hasSale && (
        <span className="rounded-md bg-neutral-100 px-2 py-0.5 text-xs font-semibold text-neutral-700">
          {Math.round(((list - sale) / list) * 100)}% off
        </span>
      )}
    </div>
  );
}

function scrollToPricing() {
  scrollToCoursePricingSection();
}

type Props = {
  courseId: string;
  slug: string;
  title: string;
  fallbackPrice: CoursePrice;
  /** `planCount` / `fromPrice` from the course detail (`docs/07` Courses (public)). */
  planSummary?: { planCount?: number | undefined; fromPrice?: number | null | undefined };
  eligibility?: Course["eligibility"];
  locale: Locale;
  firstLessonId: string | null;
};

export function CourseBottomCta({
  courseId,
  slug,
  title,
  fallbackPrice,
  planSummary,
  eligibility,
  locale,
  firstLessonId,
}: Props) {
  const t = useTranslations("courseDetail");
  const { plans, selectedPlan, selectedPlanId, mode } = useCoursePricing();
  const { isAuthenticated, getToken } = useAuth();
  const hasEligibilityGate = !!eligibility;
  const { data: eligibilityStatus } = useCourseEligibilityStatus(
    { slug, eligibility },
    isAuthenticated && hasEligibilityGate,
  );
  const eligibilityPassed =
    hasEligibilityGate && isAuthenticated && eligibilityStatus?.passed === true;
  const eligibilityLocked = hasEligibilityGate && !eligibilityPassed;
  const eligibilityQuizSlug =
    eligibilityStatus?.quiz?.slug ?? eligibility?.quizSlug ?? "";
  const eligibilityAttemptHref = `/${locale}/quiz-exams/${eligibilityQuizSlug}/attempt`;
  const eligibilityCtaHref = isAuthenticated
    ? eligibilityAttemptHref
    : signInHref(locale, eligibilityAttemptHref);
  const token = getToken();
  const { data: library } = useLibrary(token ?? undefined);
  const ownedCourse = library?.courses.find((c) => c.courseId === courseId);
  const isOwned = !!ownedCourse;

  const activePrice = selectedPlan?.price ?? fallbackPrice;
  const selectedName =
    getLocalizedField(selectedPlan?.name_en, selectedPlan?.name_bn, locale) ??
    selectedPlan?.name_en ??
    "";
  const copy = getOwnershipCopy(locale);
  const pricingUiModel = useCoursePlanPricingUiModel(
    ownedCourse,
    isAuthenticated,
    plans,
  );

  const selectedIsUpgradeTarget =
    !!selectedPlan && isPlanIdActiveUpgradeTarget(selectedPlan._id, pricingUiModel);

  const selectedPlanOwnedState =
    selectedPlan && isAuthenticated && isOwned
      ? resolvePricingUiOwnedState(selectedPlan, pricingUiModel, {
          isAuthenticated,
          isOwned,
        })
      : null;

  const nominalLegacyPlan = pricingUiModel.nominalLegacyPlan;
  const nominalLegacyName =
    nominalLegacyPlan &&
    (getLocalizedField(
      nominalLegacyPlan.name_en,
      nominalLegacyPlan.name_bn,
      locale,
    ) ??
      nominalLegacyPlan.name_en);

  const ownedTierChipName =
    pricingUiModel.effectiveOwnedPlanForCopy &&
    (getLocalizedField(
      pricingUiModel.effectiveOwnedPlanForCopy.name_en,
      pricingUiModel.effectiveOwnedPlanForCopy.name_bn,
      locale,
    ) ??
      pricingUiModel.effectiveOwnedPlanForCopy.name_en);

  const bottomPeerNotice =
    selectedPlan && selectedPlanOwnedState
      ? selectedPlanOwnedState.kind === "owned-other" && !selectedPlanOwnedState.upgradeable
        ? t("planOwnership.coveredByPlan", {
            planName: ownedTierChipName ?? t("planOwnership.yourPlanShort"),
          })
        : selectedPlanOwnedState.kind === "owned-legacy-peer" && nominalLegacyName
          ? t("planOwnership.enrolledInPlanChip", { planName: nominalLegacyName })
          : undefined
      : undefined;

  const upgradePathBlocked =
    !!isAuthenticated &&
    !!isOwned &&
    !!selectedPlan &&
    resolvePlanPricingUiKind(selectedPlan, pricingUiModel, {
      isAuthenticated,
      isOwned,
    }) === "LOWER_OR_SIDEWAYS";

  const upgradeUnavailableReason = bottomPeerNotice
    ? undefined
    : upgradePathBlocked
      ? "This tier is not offered as an upgrade from your current access. You can continue learning on your included plan or pick an upgrade tier listed for your level."
      : undefined;
  const upgradeUnavailableLabel = bottomPeerNotice
    ? undefined
    : upgradePathBlocked
      ? `${copy.upgradePrefix} ${selectedName}`
      : undefined;
  const upgradeLabel =
    selectedIsUpgradeTarget && selectedPlan
      ? formatTierUpgradeCtaLabel(selectedPlan, pricingUiModel, {
          upgradePay: (amount) => t("planOwnership.upgradePay", { amount }),
          upgradeFree: () => t("planOwnership.upgradeFree"),
        })
      : undefined;

  const multiAwaitingPick =
    mode === "multi" &&
    selectedPlanId === null &&
    !pricingUiModel.isLegacyEnrollmentWithoutPlanId;

  const fromAmountLabel = useMemo(() => {
    if (plans.length === 0) return "";
    if (plans.some((p) => p.price.isFree)) return "Free";
    let min = Infinity;
    let currency = plans[0]!.price.currency;
    for (const p of plans) {
      const list = coalesceCourseMoney(p.price.list);
      const sale = p.price.sale;
      const amount =
        sale !== undefined && Number.isFinite(sale) && sale < list ? sale : list;
      if (amount < min) {
        min = amount;
        currency = p.price.currency;
      }
    }
    if (min === Infinity) return "";
    const sym = currency === "BDT" ? "৳" : "$";
    return `${sym}${formatCourseMoneyEnIn(min)}`;
  }, [plans]);

  return (
    <div className="rounded-2xl border border-neutral-200/90 bg-white px-8 py-12 text-center shadow-[0_8px_30px_rgba(0,0,0,0.05)]">
      <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-neutral-500">
        {t("finalCtaEyebrow")}
      </p>
      <p className="mb-2 text-[22px] font-semibold leading-snug text-neutral-950 md:text-[24px]">
        {multiAwaitingPick ? t("finalCtaChoosePlanTitle") : t("finalCtaReadyTitle")}
      </p>
      <p className="mx-auto mb-8 max-w-xl text-[15px] leading-relaxed text-neutral-600">
        {multiAwaitingPick ? t("finalCtaChoosePlanBody") : title}
      </p>
      {isAuthenticated && isOwned && !multiAwaitingPick ? (
        <div className="mx-auto mb-8 max-w-lg text-left">
          <CoursePricingOwnedStatusBanner
            locale={locale}
            planDisplayName={
              (pricingUiModel.effectiveOwnedPlanForCopy &&
                (getLocalizedField(
                  pricingUiModel.effectiveOwnedPlanForCopy.name_en,
                  pricingUiModel.effectiveOwnedPlanForCopy.name_bn,
                  locale,
                ) ?? pricingUiModel.effectiveOwnedPlanForCopy.name_en)) ||
              t("planOwnership.yourPlanShort")
            }
            hasActiveUpgradeTarget={pricingUiModel.hasActiveUpgradeTarget}
          />
        </div>
      ) : null}
      <div className="flex flex-wrap items-center justify-center gap-4">
        {eligibilityPassed && (
          <p className="rounded-full bg-emerald-50 px-4 py-2 text-[14px] font-semibold text-emerald-700">
            {t("eligibilityVerified")}
          </p>
        )}
        {multiAwaitingPick ? (
          <div className="flex w-full max-w-md flex-col items-center gap-4 sm:flex-row sm:justify-center">
            <p className="text-[14px] font-medium text-neutral-500">
              {t("stickyPillFromPrice", { amount: fromAmountLabel })}
            </p>
            <Button
              type="button"
              className="h-12 min-w-[10rem] rounded-[10px] px-8 text-[15px] font-semibold"
              onClick={scrollToPricing}
            >
              {t("finalCtaComparePlans")}
            </Button>
          </div>
        ) : isAuthenticated &&
          isOwned &&
          !pricingUiModel.hasActiveUpgradeTarget &&
          pricingUiModel.isLegacyEnrollmentWithoutPlanId ? (
          <p className="rounded-full bg-emerald-50 px-4 py-2 text-[14px] font-semibold text-emerald-700">
            {copy.legacyBannerTitle}
          </p>
        ) : (
          <div className={cn("flex flex-col items-center gap-1")}>
            {mode === "multi" && selectedName ? (
              <p className="text-[13px] font-semibold text-neutral-500">
                {t("finalCtaSelectedPlanLabel", { planName: selectedName })}
              </p>
            ) : null}
            <PriceDisplay
              price={activePrice}
              size="lg"
              locale={locale}
              planSummary={selectedPlan ? undefined : planSummary}
            />
          </div>
        )}
        {!multiAwaitingPick &&
        !(isAuthenticated && isOwned && !pricingUiModel.hasActiveUpgradeTarget && pricingUiModel.isLegacyEnrollmentWithoutPlanId) ? (
          eligibilityLocked ? (
            <Button className="h-12 w-auto min-w-[10rem] rounded-[10px] px-8" asChild>
              <Link href={eligibilityCtaHref}>{t("eligibilityCtaPrimary")}</Link>
            </Button>
          ) : (
            <CourseEnrollCta
              courseId={courseId}
              slug={slug}
              price={activePrice}
              pricingPlanId={selectedPlan?._id}
              enrollLabel={
                selectedName ? t("enrollInPlan", { planName: selectedName }) : undefined
              }
              upgradeToPlanId={selectedIsUpgradeTarget ? selectedPlan?._id : undefined}
              upgradeLabel={upgradeLabel}
              upgradeUnavailableReason={upgradeUnavailableReason}
              upgradeUnavailableLabel={upgradeUnavailableLabel}
              enrolledPeerNotice={bottomPeerNotice}
              locale={locale}
              firstLessonId={firstLessonId}
              className="h-12 w-auto min-w-[10rem] rounded-[10px] px-8"
            />
          )
        ) : null}
      </div>
    </div>
  );
}
